import { Suspense, lazy } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/app/queryClient'
import { AuthProvider } from '@/features/auth/AuthContext'
import { ProtectedRoute } from '@/components/common/ProtectedRoute'
import AppLayout from '@/components/layout/AppLayout'
import LoginPage from '@/pages/LoginPage'
import SignupPage from '@/pages/SignupPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import DashboardPage from '@/pages/DashboardPage'
import CompaniesPage from '@/pages/CompaniesPage'
import CompanyDetailPage from '@/pages/CompanyDetailPage'
import UsersPage from '@/pages/UsersPage'
import UserDetailPage from '@/pages/UserDetailPage'
import ProfilePage from '@/pages/ProfilePage'
import PrivilegeManagementPage from '@/pages/PrivilegeManagementPage'
import PermissionManagementPage from '@/pages/PermissionManagementPage'
import FeatureFlagsPage from '@/pages/FeatureFlagsPage'
import ConsolidationPage from '@/pages/ConsolidationPage'
import AlarmTopicsPage from '@/pages/AlarmTopicsPage'
import ReportGroupsPage from '@/pages/ReportGroupsPage'
import ControllerStatusPage from '@/pages/ControllerStatusPage'
import MaintenanceNotesPage from '@/pages/MaintenanceNotesPage'
import MonthlyReportsPage from '@/pages/MonthlyReportsPage'
import SiteInfoPage from '@/pages/SiteInfoPage'
import ControllersPage from '@/pages/ControllersPage'
import IotManagementPage from '@/pages/IotManagementPage'
import ControllerFormPage from '@/pages/ControllerFormPage'
import MicrogridsPage from '@/pages/MicrogridsPage'
import FleetAlarmSubscriptionsPage from '@/pages/FleetAlarmSubscriptionsPage'
import FleetAlarmSubscriptionsManagePage from '@/pages/FleetAlarmSubscriptionsManagePage'
import SystemAlarmSubscriptionsPage from '@/pages/SystemAlarmSubscriptionsPage'
import SystemAlarmSubscriptionsManagePage from '@/pages/SystemAlarmSubscriptionsManagePage'
import LoadingState from '@/components/common/LoadingState'

// Lazy: both pull in Highcharts (~400KB via ChartContainer), which every
// other page should not have to download on first load. Add future
// chart-bearing pages here the same way rather than importing them eagerly
// above.
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'))
const SettingsAlarmsPage = lazy(() => import('@/pages/SettingsAlarmsPage'))
const DeviceDetailsPage = lazy(() => import('@/pages/DeviceDetailsPage'))

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="companies" element={<CompaniesPage />} />
              <Route path="companies/:clientId" element={<CompanyDetailPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:userId" element={<UserDetailPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="privileges" element={<PrivilegeManagementPage />} />
              <Route path="permissions" element={<PermissionManagementPage />} />
              <Route path="feature-flags" element={<FeatureFlagsPage />} />
              <Route path="consolidation" element={<ConsolidationPage />} />
              <Route path="alarm-topics" element={<AlarmTopicsPage />} />
              <Route path="report-groups" element={<ReportGroupsPage />} />
              <Route path="controller-status" element={<ControllerStatusPage />} />
              <Route path="maintenance" element={<MaintenanceNotesPage />} />
              <Route path="monthly-reports" element={<MonthlyReportsPage />} />
              <Route path="site-info" element={<SiteInfoPage />} />
              <Route path="controllers" element={<ControllersPage />} />
              <Route path="iot-management" element={<IotManagementPage />} />
              <Route path="controllers/new" element={<ControllerFormPage />} />
              <Route path="controllers/:controllerId/edit" element={<ControllerFormPage />} />
              <Route path="microgrids" element={<MicrogridsPage />} />
              <Route path="fleet-alarm-subscriptions" element={<FleetAlarmSubscriptionsPage />} />
              <Route path="fleet-alarm-subscriptions/manage" element={<FleetAlarmSubscriptionsManagePage />} />
              <Route path="system-alarm-subscriptions" element={<SystemAlarmSubscriptionsPage />} />
              <Route path="system-alarm-subscriptions/manage" element={<SystemAlarmSubscriptionsManagePage />} />
              <Route
                path="settings-alarms"
                element={
                  <Suspense fallback={<LoadingState label="Loading alarms…" />}>
                    <SettingsAlarmsPage />
                  </Suspense>
                }
              />
              <Route
                path="analytics"
                element={
                  <Suspense fallback={<LoadingState label="Loading analytics…" />}>
                    <AnalyticsPage />
                  </Suspense>
                }
              />
              <Route
                path="site-details"
                element={
                  <Suspense fallback={<LoadingState label="Loading site details…" />}>
                    <DeviceDetailsPage />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}
